interface DownloadOptions {
  /**
   * 다운로드할 때 표시될 제목 (Web Share API용)
   */
  title?: string
  /**
   * 다운로드할 때 표시될 텍스트 (Web Share API용)
   */
  text?: string
  /**
   * 파일 확장자 (자동 감지되지 않는 경우 사용)
   */
  fileExtension?: string
  /**
   * MIME 타입 (자동 감지되지 않는 경우 사용)
   */
  mimeType?: string
}

/**
 * URL에서 파일 확장자를 추출합니다.
 */
const getFileExtension = (url: string): string => {
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    const extension = pathname.split('.').pop()?.toLowerCase()
    return extension || ''
  } catch {
    return ''
  }
}

/**
 * 파일 확장자를 기반으로 MIME 타입을 반환합니다.
 * iPad Safari 호환성을 위해 이미지 타입을 더 엄격하게 설정합니다.
 */
const getMimeType = (extension: string): string => {
  const cleanExtension = extension.toLowerCase().trim()
  
  const mimeTypes: Record<string, string> = {
    // 이미지 - iPad Safari에서 잘 인식되는 타입들
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp',
    // 비디오
    mp4: 'video/mp4',
    webm: 'video/webm',
    avi: 'video/x-msvideo',
    mov: 'video/quicktime',
    // 기타
    pdf: 'application/pdf',
    zip: 'application/zip',
  }
  
  return mimeTypes[cleanExtension] || 'application/octet-stream'
}

/**
 * 파일이 이미지인지 확인합니다.
 */
const isImageFile = (extension: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
  return imageExtensions.includes(extension.toLowerCase().trim())
}

/**
 * Web Share API를 지원하는지 확인합니다.
 */
const isWebShareSupported = (): boolean => {
  return typeof navigator !== 'undefined' && 'share' in navigator
}

/**
 * 브라우저가 모바일인지 확인합니다.
 */
const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false
  
  const userAgent = navigator.userAgent.toLowerCase()
  return /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent)
}

/**
 * Web Share API를 사용하여 파일을 공유합니다.
 */
const shareFileWithWebAPI = async (
  blob: Blob,
  filename: string,
  options: DownloadOptions = {}
): Promise<void> => {
  const file = new File([blob], filename, { type: blob.type })
  
  const shareData: ShareData = {
    files: [file],
    title: options.title || '파일 공유',
    text: options.text || '파일을 저장하거나 공유하세요',
  }
  
  // Web Share API가 해당 데이터를 공유할 수 있는지 확인
  if (navigator.canShare && !navigator.canShare(shareData)) {
    throw new Error('Web Share API가 이 파일 형식을 지원하지 않습니다')
  }
  
  await navigator.share(shareData)
}

/**
 * 전통적인 다운로드 방식을 사용합니다.
 */
const downloadWithLink = (blob: Blob, filename: string): void => {
  const blobUrl = window.URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  link.style.display = 'none'
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  // 메모리 정리를 위해 짧은 지연 후 URL 해제
  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl)
  }, 100)
}

/**
 * URL에서 파일을 다운로드하는 범용 함수입니다.
 * 
 * @param url - 다운로드할 파일의 URL (S3, Google Storage 등 모든 URL 지원)
 * @param filename - 저장할 파일명 (확장자 포함)
 * @param options - 추가 옵션
 * @returns Promise<void>
 * 
 * @example
 * // 기본 사용법
 * await downloadFile('https://example.com/image.jpg', 'my-image.jpg')
 * 
 * // 옵션과 함께 사용
 * await downloadFile('https://s3.amazonaws.com/bucket/video.mp4', 'video.mp4', {
 *   title: '동영상 저장',
 *   text: '동영상을 저장하거나 공유하세요'
 * })
 */
export const downloadFile = async (
  url: string,
  filename: string,
  options: DownloadOptions = {}
): Promise<void> => {
  try {
    // URL에서 파일 확장자 추출
    const urlExtension = getFileExtension(url)
    const filenameExtension = getFileExtension(filename)
    const extension = options.fileExtension || filenameExtension || urlExtension
    
    // MIME 타입 결정
    const mimeType = options.mimeType || getMimeType(extension)

    console.log('📥 다운로드 시작:', { url, filename, extension, mimeType })
    
    // 파일 fetch
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`파일 다운로드 실패: ${response.status} ${response.statusText}`)
    }
    
    const blob = await response.blob()
    console.log('📄 원본 blob 정보:', { 
      size: blob.size, 
      type: blob.type,
      contentType: response.headers.get('content-type') 
    })
    
    // blob이 비어있는지 확인
    if (blob.size === 0) {
      throw new Error('다운로드한 파일이 비어있습니다')
    }
    
    // iPad Safari 호환성을 위해 항상 올바른 MIME 타입으로 새 Blob 생성
    const finalBlob = new Blob([blob], { type: mimeType })
    console.log('✅ 최종 blob 타입 설정:', { type: finalBlob.type, size: finalBlob.size })
    
    // File 배열로 Web Share API 시도 (모바일에서)
    if (isWebShareSupported() && isMobileDevice()) {
      try {
        // Blob을 File 객체로 변환
        const file = new File([finalBlob], filename, { type: mimeType })
        
        // File 배열로 공유 - 이렇게 해야 사진첩 옵션이 나타남!
        await navigator.share({
          files: [file],
          title: options.title || `${filename} 공유`,
          text: options.text || '파일을 저장하거나 공유하세요'
        })
        
        console.log('✅ Web Share API (File)로 공유 완료')
        return
      } catch (shareError) {
        console.log('⚠️ Web Share API (File) 실패, 전통적인 다운로드로 전환:', shareError)
      }
    }
    
    // 전통적인 다운로드 방식
    downloadWithLink(finalBlob, filename)
    console.log('✅ 전통적인 방식으로 다운로드 완료')
    
  } catch (error) {
    console.error('❌ 다운로드 실패:', error)
    
    // 최후의 수단: 새 탭에서 URL 열기
    try {
      const newWindow = window.open(url, '_blank')
      if (!newWindow) {
        throw new Error('새 창을 열 수 없습니다')
      }
      console.log('✅ 새 탭에서 파일 열기로 대체')
    } catch (fallbackError) {
      console.error('❌ 모든 다운로드 방법 실패:', fallbackError)
      throw new Error(`파일 다운로드에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }
}

/**
 * 여러 파일을 순차적으로 다운로드합니다.
 * 
 * @param downloads - 다운로드할 파일 정보 배열
 * @param delay - 다운로드 간 지연 시간 (밀리초)
 */
export const downloadMultipleFiles = async (
  downloads: Array<{
    url: string
    filename: string
    options?: DownloadOptions
  }>,
  delay: number = 200
): Promise<void> => {
  for (let i = 0; i < downloads.length; i++) {
    const { url, filename, options } = downloads[i]
    
    try {
      await downloadFile(url, filename, options)
      
      // 마지막 파일이 아니면 지연
      if (i < downloads.length - 1) {
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    } catch (error) {
      console.error(`다중 다운로드 중 오류 (${i + 1}/${downloads.length}):`, error)
    }
  }
}